import { classNames } from '@zambezi/sdk/common'
import PropTypes from 'prop-types'
import React, { Children, forwardRef, useMemo, useRef } from 'react'
import { noop, useMenu } from './menu'

/**
 * Create a React Nav component
 *
 * @param {import('./interfaces').NavProps} props
 * @param {import('react').Ref} ref
 * @returns {JSX.Element}
 */
function nav (
  {
    children,
    className,
    'aria-label': ariaLabel,
    onItemSelected,
    orientation,
    prefix,
    suffix,
    withAutoOpen
  },
  ref
) {
  const isVertical = orientation === 'vertical'
  const body = useMemo(
    () =>
      isVertical ? Children.map(children, applyNavMenuItemIcon) : children,
    [children, isVertical]
  )
  /** @type import('react').RefObject<HTMLElement> */
  const elementRef = useRef()

  const { content, setOpenItem } = useMenu(
    {
      children: body,
      onItemSelect: handleSelect,
      orientation,
      elementRef,
      withAutoOpen,
      onItemKeyDown: noop
    },
    ref
  )

  return (
    <nav
      className={classNames('zb-nav', `zb-nav-${orientation}`, className)}
      aria-label={ariaLabel}
      ref={elementRef}
    >
      {prefix}
      {content}
      {suffix}
    </nav>
  )

  /**
   * @param {import('react').SyntheticEvent} event
   */
  function handleSelect (event) {
    onItemSelected(event)
    !withAutoOpen && setOpenItem(-1)
  }
}

/**
 * @param {import('react').ReactChild=} child
 * @returns {import('react').ReactChild}
 */
function applyNavMenuItemIcon (child) {
  const { type, props } = child || {}
  if (child && props) {
    return Object.assign({}, child, {
      props: Object.assign(
        {},
        props,
        type && type.componentName === 'zb.NavMenuItem'
          ? {
            iconProps: Object.assign(
              {
                name: 'chev-down-xsmall'
              },
              props.iconProps
            )
          }
          : {},
        {
          children: Children.map(props.children, applyNavMenuItemIcon)
        }
      )
    })
  }
  return child
}

const Nav = forwardRef(nav)
Nav.displayName = 'Nav'

Nav.propTypes = {
  onItemSelected: PropTypes.func,
  'aria-label': PropTypes.string,
  orientation: PropTypes.oneOf(['horizontal', 'vertical']),
  prefix: PropTypes.node,
  suffix: PropTypes.node,
  withAutoOpen: PropTypes.bool
}
Nav.defaultProps = {
  onItemSelected: noop,
  orientation: 'horizontal'
}

export default Nav
